// BUILD-046 tasks 8.16 / 8.17 (supporting model): text shown on the rows of the Group / Shared view and the Oversight view.
//
// (Reconcile pass, 2026-10-20: `describeDue` below is unchanged and still reads only the server's flag. The Shared and Team rows write "Overdue" through
// `rowIsOverdueOn` of taskOrdering.ts, which uses `describeDue` for that flag and adds, for an UNCLAIMED task that never carries it, a past Task due date.)
//
// Dates (Phoenix-date correctness is slice S2): the server decides what is overdue and sends `overdue`; the client displays that flag and
// NEVER parses a date-only string with `new Date()` (an ISO date-only string parses as UTC midnight, which shows the previous day in a
// UTC-7 zone: design "Phoenix dates"). `formatDueDate` therefore splits the `YYYY-MM-DD` string. Full ISO timestamps (`updatedAt`) are
// instants, so `relativeAge` may parse those.
//
// Pure module: no React, no I/O, no clock (the caller supplies `now`); its only runtime import is the pure display helper displayNames.ts (D1). Erasable TypeScript only.

import { personDisplay, stripTestMarker } from './displayNames';

const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;

/** `2026-10-18` becomes `10/18/2026` by splitting the string; anything that is not a zero-padded `YYYY-MM-DD` date gives null. */
export function formatDueDate(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const match = ISO_DATE.exec(value);
  if (match === null) return null;
  const month = Number(match[2]);
  const day = Number(match[3]);
  if (month < 1 || month > 12 || day < 1 || day > 31) return null;
  return `${match[2]}/${match[3]}/${match[1]}`;
}

export interface DueInput {
  dueDate?: unknown;
  status?: string;
  overdue?: unknown;
  lifecycle?: string;
}

export interface DueView {
  /** `Due 10/18/2026`, or null when the task has no (usable) due date. */
  text: string | null;
  /** The server's flag, shown only on an open task; never computed here. */
  overdue: boolean;
}

export function describeDue(item: DueInput): DueView {
  const formatted = formatDueDate(item.dueDate);
  const open = item.status === 'active' || item.status === 'in_progress' || item.status === 'waiting' || item.status === 'blocked';
  return { text: formatted === null ? null : `Due ${formatted}`, overdue: open && item.lifecycle !== 'cancelled' && item.overdue === true };
}

/** `just now`, `5m ago`, `3h ago`, `2d ago`, and the month/day/year of an older instant. An unreadable timestamp gives ''. */
export function relativeAge(iso: unknown, nowMs: number): string {
  if (typeof iso !== 'string' || iso === '') return '';
  const then = Date.parse(iso);
  if (!Number.isFinite(then) || !Number.isFinite(nowMs)) return '';
  const minutes = Math.floor((nowMs - then) / 60000);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  if (hours < 24) return `${hours}h ago`;
  if (days < 7) return `${days}d ago`;
  // An older instant is shown as its Phoenix calendar date (the company's day), not the browser's zone.
  return new Date(then).toLocaleDateString('en-US', { timeZone: 'America/Phoenix', year: 'numeric', month: 'numeric', day: 'numeric' });
}

export interface QueueWordingInput {
  unclaimed?: boolean;
  groupRefs?: readonly { name?: string }[];
}

/**
 * The recorded queue label for a task issued to a named Work_Group (Decision D-L, L1; Req 3.7, 3.19, 4.25, 7.11): "Anyone currently in
 * <Group> can take this." It is the LIVE queue (people who join the group can take the task, people removed from it cannot), so it is said
 * of an unclaimed task only. It is null for a task nobody can take any more (claimed, or issued to people only), and the Everyone form has a
 * different sentence (fixed at issue: issuedRequestsModel.fixedAtIssueSentence).
 */
export function queueWording(item: QueueWordingInput): string | null {
  if (item.unclaimed !== true) return null;
  const names: string[] = [];
  // The group names are SHOWN without the "(FICTIONAL)" test marker (displayNames.stripTestMarker); the stored names are not changed.
  for (const g of item.groupRefs ?? []) if (typeof g?.name === 'string' && stripTestMarker(g.name) !== '') names.push(stripTestMarker(g.name));
  if (names.length === 0) return null;
  const joined = names.length === 1 ? names[0] : `${names.slice(0, -1).join(', ')} or ${names[names.length - 1]}`;
  return `Anyone currently in ${joined} can take this.`;
}

export interface PersonRef {
  name?: string;
  /** The stable employee id (the route's recipientSummary carries it); the initials circles take their colour from it. */
  employeeId?: string;
}

export interface RecipientInput {
  recipientLabel?: string;
  groupRefs?: readonly { name: string }[];
  recipientSummary?: readonly PersonRef[];
}

/**
 * The recipient label of a Shared_Task row (Req 4.25, 7.4, 7.11): the server's `recipientLabel` as given; without it, the Work_Group names
 * and then every selected person's name (the names the route returned), joined by a comma. Null when nothing is known (the row then shows
 * no recipient text rather than an invented one).
 */
export function recipientLabelOf(item: RecipientInput): string | null {
  return recipientTexts(item, 'short');
}

/**
 * The same recipient label with every person's FULL name (the group names and the people, the "(FICTIONAL)" marker removed): the hover and focus text of the short
 * label, or null when it adds nothing to it.
 */
export function recipientFullLabelOf(item: RecipientInput): string | null {
  const full = recipientTexts(item, 'full');
  return full !== null && full !== recipientTexts(item, 'short') ? full : null;
}

/** The parts of a task's recipients: the stored group names and people names (those with a name), the label they make when joined, and the server's own label (null when absent). */
function recipientParts(item: RecipientInput): { groupNames: string[]; people: string[]; stored: string; given: string | null } {
  const groupNames: string[] = [];
  for (const g of item.groupRefs ?? []) if (typeof g?.name === 'string' && g.name.trim() !== '') groupNames.push(g.name);
  const people: string[] = [];
  for (const r of item.recipientSummary ?? []) if (typeof r?.name === 'string' && r.name.trim() !== '') people.push(r.name);
  const stored = [...groupNames, ...people].join(', ');
  const given = typeof item.recipientLabel === 'string' && item.recipientLabel.trim() !== '' ? item.recipientLabel : null;
  return { groupNames, people, stored, given };
}

/** A person a task was sent to, as an initials circle needs them. `employeeId` is '' when the route did not carry one (the circle then keys its colour on the name). */
export interface RecipientPerson {
  employeeId: string;
  name: string;
}

/** The people a task was sent to (the route's recipientSummary), in the server's order, those with a name only. Increment 1b-i draws them as initials circles. */
export function recipientPeopleOf(item: RecipientInput): RecipientPerson[] {
  const out: RecipientPerson[] = [];
  for (const r of item.recipientSummary ?? []) if (typeof r?.name === 'string' && r.name.trim() !== '') out.push({ employeeId: typeof r.employeeId === 'string' ? r.employeeId : '', name: r.name });
  return out;
}

/**
 * The words of "To ..." that are NOT people: the group names, the "(FICTIONAL)" marker removed (a group is not a person, so it stays text beside the people's circles); null when there
 * are none. When the server's own label is anything other than the group names and people it stored, it cannot be split and is shown as given (marker removed).
 */
export function recipientGroupLabelOf(item: RecipientInput): string | null {
  const { groupNames, stored, given } = recipientParts(item);
  if (given !== null && given !== stored) {
    const shown = stripTestMarker(given);
    return shown === '' ? null : shown;
  }
  const shownNames = groupNames.map((name) => stripTestMarker(name)).filter((name) => name !== '');
  return shownNames.length > 0 ? shownNames.join(', ') : null;
}

/**
 * The recipient label as it is SHOWN (D1 display cleanup). Group names lose the "(FICTIONAL)" test marker; people are written First name + last initial (or the first
 * name alone: displayNames.personDisplay). The server's label is the group names and then the people joined by ", " (task-service recipientLabel); when it is exactly that
 * the label is rebuilt from the same parts so each person can be shortened. A label that is anything else is shown as given, with only the marker removed.
 */
function recipientTexts(item: RecipientInput, form: 'short' | 'full'): string | null {
  const { groupNames, people, stored, given } = recipientParts(item);
  if (given !== null && given !== stored) {
    const shown = stripTestMarker(given);
    return shown === '' ? null : shown;
  }
  const shownNames = [...groupNames.map((name) => stripTestMarker(name)), ...people.map((name) => (form === 'short' ? personDisplay(name).short : personDisplay(name).full))].filter((name) => name !== '');
  return shownNames.length > 0 ? shownNames.join(', ') : null;
}
